import { Alert, Box, Button, CircularProgress, Collapse, DialogActions, DialogContent, DialogTitle, Grid2 as Grid, IconButton, ListItemIcon, Menu, MenuItem, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { HighlightSelectBox, MyPaper, SectionPaper, TextField } from "../../../components";
import { ExchangeData, useCalculateCostingMutation, useLazyGetBOMTreeByIdQuery, useUpdatePricingMutation } from "../../../redux/services";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { closeDialog, openDialog } from "../../../redux/slices/dialog";
import { TypeOptions } from "../summary/editSummary";
import { SelectBoxOption } from "../../../interfaces";
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams, GridRowModel, GridRowSelectionModel, GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-premium";
import { StyledDataGrid } from "../../workbook/actions/actionTable";
import { ArrowDropDown, CurrencyExchange, DeleteOutline, Edit, ExpandLess, ExpandMore, Info, Warning } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { chartOptions, defaultCostingSummary, findFirstCustomCosts, OperationTypes, pricingOutdatedUpdateFn, updateTreeWithFlattenedData } from "../utils";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { CostGroup, CustomCost, formatCurrency } from "pdf-generator";
import { ChildProps, ValidationError } from "..";
import copy from "fast-copy";
import { ErrorComponent } from "../components/ErrorComponent";
import Chart from "react-google-charts";
import moment from "moment";
import { getUserSelectedCurrency } from "../../../utils";

const defaultOption = { label: "Markup in (%)", value: "markup_percentage" };

interface MenuOptions {
    editColumn: () => void;
    deleteColumn: () => void;
}

export const CustomCostColumn = ({ col_name, tooltip_title, menuOptions }: { col_name: string; tooltip_title: string; menuOptions?: MenuOptions }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Stack
            direction={"row"}
            spacing={1}
            sx={{
                alignItems: "center",
                width: "100%"
            }}>
            <Stack
                direction={"row"}
                sx={{
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}>
                <Tooltip arrow title={tooltip_title} placement="top">
                    <Stack
                        direction={"row"}
                        sx={{
                            gap: 1,
                            alignItems: "center"
                        }}>
                        <Info color="action" fontSize='small' />
                        <Typography variant="body2" ><strong>{col_name}</strong></Typography>
                    </Stack>
                </Tooltip>


                {menuOptions &&
                    <>
                        <Stack
                            onClick={handleClick}
                            sx={{
                                alignItems: 'center',
                                cursor: "pointer"
                            }}>
                            <ArrowDropDown fontSize={"medium"} color="primary" />
                        </Stack>
                        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                            <MenuItem onClick={() => {
                                menuOptions.editColumn();
                                handleClose();
                            }}>
                                <ListItemIcon>
                                    <Edit color="primary" fontSize="small" />
                                </ListItemIcon>
                                Edit
                            </MenuItem>
                            <MenuItem onClick={() => {
                                menuOptions.deleteColumn();
                                handleClose();
                            }}>
                                <ListItemIcon>
                                    <DeleteOutline color="error" fontSize="small" />
                                </ListItemIcon>
                                Delete
                            </MenuItem>
                        </Menu>
                    </>
                }

            </Stack>
        </Stack>
    );
};

const BatchUpdateColumn = ({ onBatchUpdate, fieldOptions }: { onBatchUpdate: (selectedOption: string, value: number) => void, fieldOptions: SelectBoxOption[] }) => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [selectedOption, setSelected] = React.useState<SelectBoxOption>(defaultOption);
    const [enteredValue, setValue] = React.useState<string | number>(0);

    const valid = () => {
        let isValid = false;

        if (selectedOption?.value && Number(enteredValue) >= 0 && enteredValue !== "") {
            isValid = true;
        } else {
            enqueueSnackbar("Please enter a valid value", { variant: "warning" });
        }

        return isValid;
    }

    return (<>
        <DialogTitle>
            <Typography
                variant="h6"
                color="textPrimary"
                sx={{
                    width: "100%",
                    fontFamily: 'htrts_medium',
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                Batch Update
            </Typography>
        </DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
                <HighlightSelectBox
                    disableClearable
                    label={"Column"}
                    margin={"none"}
                    fullWdith
                    value={selectedOption}
                    options={fieldOptions}
                    onChange={(data: SelectBoxOption) => setSelected(data)}
                    onKeyDown={(event) => {
                        event.stopPropagation();
                    }}
                />
                <TextField
                    label={"Value"}
                    fullWidth
                    disabled={!selectedOption}
                    placeholder="Enter value"
                    type="number"
                    size="small"
                    value={enteredValue}
                    onChange={(e) => {
                        setValue(e.target.value)
                    }}
                    onKeyDown={(event) => {
                        event.stopPropagation();
                    }}
                />
            </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center !important', mb: 2 }}>
            <Stack
                sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" }
                }}>
                <Button
                    variant="outlined"
                    onClick={() => dispatch(closeDialog())}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={() => {
                        if (valid()) {
                            onBatchUpdate(selectedOption?.value as string, Number(enteredValue));
                            dispatch(closeDialog())
                        }
                    }}
                >
                    Apply All
                </Button>
            </Stack>
        </DialogActions>
    </>);
}

const AddEditColumn = ({ onAddNewColumn, editDetails = {} }: { onAddNewColumn: (value: any) => void, editDetails?: Partial<CustomCost & { position?: number }> }) => {
    const dispatch = useAppDispatch();

    const { description, subtract, position, type, value } = editDetails;

    const [state, setState] = useState({
        description: description || "",
        position: position ?? 0,
        subtract: subtract || false,
        type: type || "fixed",
        value: value || 0 as string | number,
    });

    const [errors, setErrors] = useState({
        description: false,
        type: false,
        value: false,
    });

    const updateState = (key: string, value: string | number) => {
        setState((p) => ({ ...p, [key]: value }))
        setErrors((p) => ({ ...p, [key]: !value }))
    }

    const valid = () => {
        const obj = {
            description: !state.description.trim(),
            value: state.value === "" || Number(state.value) < 0,
            type: !state.type,
        };
        setErrors(obj);
        return Object.values(obj).every(o => o === false);
    }

    return (<>
        <DialogTitle>
            <Typography
                variant="h6"
                color="textPrimary"
                sx={{
                    width: "100%",
                    fontFamily: 'htrts_medium',
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                Add New Column
            </Typography>
        </DialogTitle>
        <DialogContent>
            <Stack sx={{ mt: 1 }} gap={2}>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <TextField
                            label={"Description"}
                            value={state.description}
                            required
                            fullWidth
                            size={"small"}
                            variant="outlined"
                            placeholder={"Enter Description"}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                updateState("description", e.target.value)
                            }
                            error={errors.description}
                            helperText={
                                errors.description ? "Please enter the description" : ""
                            }
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            label={"Value"}
                            value={state.value}
                            required
                            fullWidth
                            type={"number"}
                            size={"small"}
                            variant="outlined"
                            placeholder={"Enter Value"}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                updateState("value", e.target.value)
                            }
                            onKeyDown={(event) => {
                                event.stopPropagation();
                            }}
                            error={errors.value}
                            helperText={
                                errors.value ? "Please enter the value" : ""
                            }
                        />
                    </Grid>

                    <Grid size={12}>
                        <HighlightSelectBox
                            disableClearable
                            label={"Type"}
                            margin={"none"}
                            fullWdith
                            value={TypeOptions.find(ty => ty.value === state.type) ?? null}
                            options={TypeOptions}
                            onChange={(data: SelectBoxOption) => {
                                updateState('type', data?.value as string);
                            }}
                            error={errors.type}
                            helperText={
                                errors.type ? "Please select the type" : ""
                            }
                        />
                    </Grid>

                    <Grid size={12}>
                        <HighlightSelectBox
                            disableClearable
                            label={"Operation Type"}
                            margin={"none"}
                            fullWdith
                            value={OperationTypes.find(ty => ty.value === state.subtract) ?? null}
                            options={OperationTypes}
                            onChange={(data: SelectBoxOption) => {
                                updateState('subtract', data?.value as string);
                            }}
                        />
                    </Grid>
                </Grid>

                <Alert color="info">Custom costs will not be applied to BOMs without any overhead costs defined</Alert>

            </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center !important', mb: 2 }}>
            <Stack
                sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" }
                }}>
                <Button
                    variant="outlined"
                    onClick={() => dispatch(closeDialog())}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={() => {
                        if (valid()) {
                            onAddNewColumn({ ...state, value: Number(state.value) });
                            dispatch(closeDialog())
                        }
                    }}
                >
                    {description ? "Update" : "Add"}
                </Button>
            </Stack>
        </DialogActions>
    </>);
}

export const EditCosting = ({ readOnly, refetchCosting, refetch, costingData }: ChildProps) => {

    const { id = "" } = useParams();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [getBOMTree] = useLazyGetBOMTreeByIdQuery();
    const [updatePricing, updateRes] = useUpdatePricingMutation();
    const [calculateCost, calculationRes] = useCalculateCostingMutation();

    const isLoading = calculationRes.isLoading || updateRes.isLoading;

    const { pricing, auth } = useAppSelector(store => store);

    const DEFAULT_CURRENCY = getUserSelectedCurrency(auth.userPreferences).value;

    const { costing, hasCosting, levelsData = [] } = costingData;

    const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
    const [hasCalculated, setHasCalculated] = React.useState(hasCosting);
    const [open, updateOpen] = React.useState(false);

    const [bomCostingDetails, setDetails] = React.useState(defaultCostingSummary);
    const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);

    const commonColumns: GridColDef[] = useMemo(() => {
        return [
            {
                field: 'bom',
                width: 200,
                headerName: 'BOM Tree',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams) => {
                    return <Stack>
                        <Typography
                            component="div"
                            variant="body2"
                            sx={{
                                fontWeight: params.row?.bomId?.includes("BOM") ? "bold" : "normal",
                                marginLeft: params.row?.ml
                            }}
                        >
                            {params.row.bom?.at(-1)}
                        </Typography>
                    </Stack>
                }
            },
            {
                field: 'short_description',
                width: 320,
                headerName: 'Description',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'sum_of_assm_procs_final_total_cost_in_parent_currency',
                headerName: 'Total Overhead Cost',
                width: 250,
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"Total Overhead Cost"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>Total Overhead Cost</strong></Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {isNaN(params.row?.sum_of_assm_procs_final_total_cost_in_parent_currency) ? "" : formatCurrency(Number(params.row?.sum_of_assm_procs_final_total_cost_in_parent_currency?.toFixed(2)), params?.row?.total_assm_procs_currency, false, false) || ''}
                    </Typography>
                },
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'total_assm_procs_currency',
                headerName: 'Total Overhead Currency',
                width: 250,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'total_child_bom_cost',
                headerName: 'Total Child BOM Cost',
                width: 200,
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"Total Child BOM Cost"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>Total Child BOM Cost</strong></Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {isNaN(params.row?.total_child_bom_cost) ? "" : formatCurrency(Number(params.row?.total_child_bom_cost?.toFixed(2)), params?.row?.total_child_bom_currency, false, false) || ''}
                    </Typography>
                },
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'total_child_bom_currency',
                headerName: 'Total Child BOM Currency',
                width: 200,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'total_child_material_cost',
                headerName: 'Total Child Material Cost',
                width: 200,
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"Total Child Material Cost"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>Total Child Material Cost</strong></Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {isNaN(params.row?.total_child_material_cost) ? "" : formatCurrency(Number(params.row?.total_child_material_cost?.toFixed(2)), params?.row?.total_child_material_currency, false, false) || ''}
                    </Typography>
                },
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'total_child_material_currency',
                headerName: 'Total Child Material Currency',
                width: 250,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'total_bom_cost',
                headerName: 'Total BOM Cost',
                width: 200,
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"Total BOM Cost"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>Total BOM Cost</strong></Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {isNaN(params.row?.total_bom_cost) ? "" : formatCurrency(Number(params.row?.total_bom_cost?.toFixed(2)), params?.row?.total_bom_currency, false, false) || ''}
                    </Typography>
                },
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'total_bom_currency',
                headerName: 'Total BOM Currency',
                width: 200,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'material_cost',
                headerName: 'Material Cost',
                width: 150,
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"Material Cost"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>Material Cost</strong></Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {isNaN(params.row?.material_cost) ? "" : formatCurrency(Number(params.row?.material_cost?.toFixed(2)), params?.row?.material_currency, false, false) || ''}
                    </Typography>
                },
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'material_currency',
                headerName: 'Material Currency',
                width: 150,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'override_cost',
                headerName: 'Override Cost',
                width: 150,
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"Override Cost"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>Override Cost</strong></Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {(params?.row?.id === costing?.calculated_cost?._id || isNaN(params.row?.override_cost)) ? "" : formatCurrency(Number(params.row?.override_cost?.toFixed(2)), params?.row?.override_currency, false, false) || ''}
                    </Typography>
                },
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'override_currency',
                headerName: 'Override Currency',
                width: 150,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'spare_part_quantity',
                headerName: 'Spare Part Qty',
                width: 150,
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"Spare Part Qty"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>Spare Part Qty</strong></Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'normal_quantity',
                headerName: 'Quantity',
                width: 150,
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"Quantity"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>Quantity</strong></Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'total_quantity',
                headerName: 'Total Quantity',
                width: 150,
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"Total Quantity"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>Total Quantity</strong></Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'total_cost',
                headerName: 'Total Cost',
                width: 150,
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"Total Cost"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>Total Cost</strong></Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {isNaN(params.row?.total_cost) ? "" : formatCurrency(Number(params.row?.total_cost?.toFixed(2)), params?.row?.total_cost_currency, false, false) || ''}
                    </Typography>
                },
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'total_cost_currency',
                headerName: 'Total Cost Currency',
                width: 180,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'markup_percentage',
                headerName: 'Markup in (%)',
                width: 150,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                editable: true,
                type: 'number',
                headerClassName: 'highlight-table-row',
                // renderCell: (params: GridRenderCellParams) => {
                //     return <InputBase
                //         required
                //         placeholder="Enter value"
                //         type="number"
                //         disabled={!params?.row?.has_assembly_process_cost && params?.row?.bomId?.includes("BOM")}
                //         value={params?.row?.markup_percentage}
                //         onChange={(e) => {
                //             setCalculatedRows((prev) => prev.map(p => {
                //                 if (p.id === params.row.id) {
                //                     return {
                //                         ...p,
                //                         markup_percentage: Number(e.target.value)
                //                     }
                //                 }
                //                 return p;
                //             }))
                //             setHasCalculated(false);
                //         }}
                //         onKeyDown={(event) => {
                //             event.stopPropagation();
                //         }}
                //     />
                // }
            },
            {
                field: 'cost_in_bom_currency',
                headerName: "New Total Cost (BOM Currency)",
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"New Total Cost in BOM Currency"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>New Total Cost</strong></Typography>
                                <Typography variant={"caption"}>(BOM Currency)</Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                width: 150,
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {hasCalculated ? formatCurrency(Number(params.row?.cost_in_bom_currency?.toFixed(2)), params?.row?.bom_currency, false, false) : params?.row?.id !== costing?.calculated_cost?._id ? <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip> : null}
                    </Typography>
                }
            },
            {
                field: 'bom_currency',
                headerName: 'BOM Currency',
                width: 180,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'unit_cost_in_pricing_currency',
                headerName: "New Unit Price",
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"New Unit Price in Pricing Currency"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>New Unit Price</strong></Typography>
                                <Typography variant={"caption"}>(Pricing Currency)</Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                width: 150,
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {hasCalculated ? formatCurrency(Number(params.row?.unit_cost_in_pricing_currency?.toFixed(2)), params?.row?.total_currency, false, false) : params?.row?.id !== costing?.calculated_cost?._id ? <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip> : null}
                    </Typography>
                }
            },
            {
                field: 'markup_cost',
                headerName: "New Total Cost",
                renderHeader: (params: GridColumnHeaderParams) => {
                    return (
                        <Tooltip title={"New Total Cost in Pricing Currency"}>
                            <Stack width={"100%"} alignItems={"flex-end"}>
                                <Typography variant="body2"><strong>New Total Cost</strong></Typography>
                                <Typography variant={"caption"}>(Pricing Currency)</Typography>
                            </Stack>
                        </Tooltip>
                    );
                },
                width: 150,
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {hasCalculated ? formatCurrency(Number(params.row?.markup_cost?.toFixed(2)), params?.row?.total_currency, false, false) : params?.row?.id !== costing?.calculated_cost?._id ? <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip> : null}
                    </Typography>
                }
            },
            {
                field: 'total_currency',
                headerName: 'Currency',
                width: 150,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
            },
            {
                field: 'validation_errors',
                headerName: '',
                width: 80,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                disableExport: true,
                renderCell: (params: GridRenderCellParams) => {
                    const validationErrors = params.row?.validation_errors || [];
                    return validationErrors.length > 0 ? <Tooltip
                        title={validationErrors.map((e: string) => <><span>{e}</span><br /></>)}
                    >
                        <Warning color={"error"} />
                    </Tooltip> : <></>
                }
            },
        ]
    }, [costing?.calculated_cost?._id, hasCalculated]);

    const [calculatedRows, setCalculatedRows] = React.useState<any[]>([]);
    const [columns, setColumns] = React.useState<GridColDef[]>(commonColumns);

    const [fieldOptions, setOptions] = React.useState<SelectBoxOption[]>([defaultOption]);

    const appendChild = (index: number, data: any, row: any[], level: number | string, ml: number, errors: ValidationError[]): any[] => {

        if (data?.children_costs?.length === 0 && data?.material_costs?.length === 0) {
            return row;
        }

        data?.children_costs?.forEach((bom_children: any, bom_children_index: number) => {

            let pathId = bom_children?.sequence_id ? `${bom_children?.sequence_id}-r${bom_children?.version ?? 0}` : bom_children.child_id || bom_children._id;
            // let pathId = bom_children.sequence_id + "-r" + bom_children?.version;

            // Appending Level
            pathId = `${typeof level === "string" ? "" : "L"}${level}.${bom_children_index + 1} [${pathId}]`;

            let childLevel = `${typeof level === "string" ? "" : "L"}${level}.${bom_children_index + 1}`;

            const obj = bom_children?.assembly_process_costs?.[0]?.custom_costs?.reduce((acc: object, cur: { description: string }) => ({ ...acc, [`custom_${cur.description}`]: cur }), {});

            row.push({
                id: bom_children?._id + bom_children_index + Math.random(),
                bom: [...row[index].bom, pathId],
                bomId: bom_children.sequence_id ?? "BOM",
                short_description: bom_children?.short_description,
                sum_of_assm_procs_final_total_cost_in_parent_currency: bom_children?.sum_of_assm_procs_final_total_cost_in_parent_currency?.cost ?? 0,
                total_assm_procs_currency: `${bom_children?.sum_of_assm_procs_final_total_cost_in_parent_currency?.currency ?? DEFAULT_CURRENCY}`,
                total_child_bom_cost: bom_children?.sum_of_children_final_total_cost_in_parent_currency?.cost ?? 0,
                total_child_bom_currency: `${bom_children?.sum_of_children_final_total_cost_in_parent_currency?.currency ?? DEFAULT_CURRENCY}`,
                total_child_material_cost: bom_children?.sum_of_material_final_total_cost_in_parent_currency?.cost ?? 0,
                total_child_material_currency: `${bom_children?.sum_of_material_final_total_cost_in_parent_currency?.currency ?? DEFAULT_CURRENCY}`,
                override_cost: bom_children?.override_cost ?? 0,
                override_currency: `${bom_children?.bom_cost?.currency ?? DEFAULT_CURRENCY}`,
                total_bom_cost: bom_children?.bom_cost?.cost ?? 0,
                total_bom_currency: `${bom_children?.bom_cost?.currency ?? DEFAULT_CURRENCY}`,
                spare_part_quantity: bom_children?.spare_part_quantity ?? 0,
                normal_quantity: bom_children?.normal_quantity ?? 1,
                total_quantity: bom_children?.total_quantity ?? 1,
                total_cost: bom_children?.total_cost?.cost ?? 0,
                total_cost_currency: bom_children?.total_cost?.currency ?? DEFAULT_CURRENCY,
                markup_percentage: bom_children?.assembly_process_costs?.[0]?.markup_percentage ?? 0,
                cost_in_bom_currency: bom_children?.final_total_cost_in_parent_currency?.cost ?? 0,
                bom_currency: `${bom_children?.final_total_cost_in_parent_currency?.currency ?? DEFAULT_CURRENCY}`,
                markup_cost: bom_children?.final_total_cost_in_pricing_currency?.cost ?? 0,
                total_currency: `${bom_children?.final_total_cost_in_pricing_currency?.currency ?? DEFAULT_CURRENCY}`,
                unit_cost_in_pricing_currency: bom_children?.unit_cost_in_pricing_currency?.cost ?? 0,
                child_id: bom_children?.child_id,
                has_assembly_process_cost: true,
                validation_errors: bom_children?.validation_errors ?? [],
                // has_assembly_process_cost: bom_children?.assembly_process_costs?.length > 0 || false,
                ml: ml,
                ...obj
            });

            if (bom_children?.validation_errors) {
                errors.push({
                    _id: bom_children?._id,
                    sequence_id: `${bom_children?.sequence_id ?? ""}-r${bom_children?.version ?? 0}`,
                    errors: bom_children?.validation_errors
                })
            }

            row.concat(appendChild(row.length - 1, bom_children, row, childLevel, ml + 1, errors));
        });

        data?.material_costs?.forEach((bom_material: any, bom_material_index: number) => {

            let pathId = bom_material?.sequence_id ? `${bom_material?.sequence_id}-r${bom_material?.version ?? 0}` : bom_material.child_id || bom_material._id;
            // let pathId = bom_material.sequence_id + "-r" + bom_material?.version;

            pathId = `${typeof level === "string" ? "" : "L"}${level}.${(data?.children_costs?.length || 0) + bom_material_index + 1} [${pathId}]`;

            const obj = bom_material?.custom_costs?.reduce((acc: object, cur: { description: string }) => ({ ...acc, [`custom_${cur.description}`]: cur }), {});

            row.push({
                id: bom_material?._id + bom_material_index + Math.random(),
                bom: [...row[index].bom, pathId],
                bomId: bom_material.sequence_id ?? "Material",
                short_description: bom_material?.short_description,
                material_cost: bom_material?.material_cost?.cost ?? 0,
                material_currency: `${bom_material?.material_cost?.currency ?? DEFAULT_CURRENCY}`,
                override_cost: bom_material?.override_cost ?? 0,
                override_currency: `${bom_material?.material_cost?.currency ?? DEFAULT_CURRENCY}`,
                spare_part_quantity: bom_material?.spare_part_quantity ?? 0,
                normal_quantity: bom_material.normal_quantity ?? 1,
                total_quantity: bom_material?.total_quantity ?? 1,
                total_cost: bom_material.total_cost?.cost ?? 0,
                total_cost_currency: bom_material?.total_cost?.currency ?? DEFAULT_CURRENCY,
                markup_percentage: bom_material?.markup_percentage ?? 0,
                cost_in_bom_currency: bom_material?.final_total_cost_in_parent_currency?.cost ?? 0,
                bom_currency: `${bom_material?.final_total_cost_in_parent_currency?.currency ?? DEFAULT_CURRENCY}`,
                markup_cost: bom_material?.final_total_cost_in_pricing_currency?.cost ?? 0,
                total_currency: `${bom_material.final_total_cost_in_pricing_currency?.currency ?? DEFAULT_CURRENCY}`,
                unit_cost_in_pricing_currency: bom_material?.unit_cost_in_pricing_currency?.cost ?? 0,
                validation_errors: bom_material?.validation_errors ?? [],
                child_id: bom_material?.child_id,
                ml,
                ...obj
            });

            if (bom_material?.validation_errors) {
                errors.push({
                    _id: bom_material?._id,
                    sequence_id: `${bom_material?.sequence_id ?? ""}-r${bom_material?.version ?? 0}`,
                    errors: bom_material?.validation_errors
                })
            }
        });

        return row;
    };

    const massageRows = React.useCallback((res: any) => {
        if (!res._id || Object.keys(res).length <= 0) {
            setCalculatedRows([]);
        }

        const errors = [];

        let pathId = `L1 [${res?.sequence_id ? `${res?.sequence_id}-r${res?.version ?? 0}` : res?.child_id || res?._id}]`;
        // let pathId = `L1 [${res.sequence_id}-r${res.version}]`;

        const obj = res?.assembly_process_costs?.[0]?.custom_costs?.reduce((acc: object, cur: { description: string }) => ({ ...acc, [`custom_${cur.description}`]: { ...cur, value: null } }), {});

        let row = [
            {
                id: res?._id,
                bom: [pathId],
                bomId: res.sequence_id ?? "BOM",
                short_description: res?.short_description,
                sum_of_assm_procs_final_total_cost_in_parent_currency: res?.sum_of_assm_procs_final_total_cost_in_parent_currency?.cost ?? 0,
                total_assm_procs_currency: `${res?.sum_of_assm_procs_final_total_cost_in_parent_currency?.currency ?? DEFAULT_CURRENCY}`,
                total_child_bom_cost: res?.sum_of_children_final_total_cost_in_parent_currency?.cost ?? 0,
                total_child_bom_currency: `${res?.sum_of_children_final_total_cost_in_parent_currency?.currency ?? DEFAULT_CURRENCY}`,
                total_child_material_cost: res?.sum_of_material_final_total_cost_in_parent_currency?.cost ?? 0,
                total_child_material_currency: `${res?.sum_of_material_final_total_cost_in_parent_currency?.currency ?? DEFAULT_CURRENCY}`,
                total_bom_cost: res?.bom_cost?.cost ?? 0,
                total_bom_currency: `${res?.bom_cost?.currency ?? DEFAULT_CURRENCY}`,
                total_cost: res?.total_cost?.cost ?? 0,
                total_cost_currency: res?.total_cost?.currency ?? DEFAULT_CURRENCY,
                child_id: res?.child_id,
                markup_percentage: null,
                cost_in_bom_currency: res?.final_total_cost?.cost,
                bom_currency: `${res?.final_total_cost?.currency ?? DEFAULT_CURRENCY}`,
                markup_cost: res?.final_total_cost_in_pricing_currency?.cost,
                total_currency: `${res?.final_total_cost_in_pricing_currency?.currency ?? DEFAULT_CURRENCY}`,
                unit_cost_in_pricing_currency: res?.final_total_cost_in_pricing_currency?.cost ?? 0,
                has_assembly_process_cost: true,
                validation_errors: res?.validation_errors ?? [],
                // has_assembly_process_cost: res?.assembly_process_costs?.length > 0 || false,
                ...obj
            }
        ];

        if (res?.validation_errors) {
            errors.push({
                _id: res?._id,
                sequence_id: `${res?.sequence_id ?? ""}-r${res?.version ?? 0}`,
                errors: res?.validation_errors
            })
        }

        setCalculatedRows(appendChild(0, res, row, 1, 1, errors));

        setValidationErrors(errors);
        // eslint-disable-next-line
    }, [costing?.calculated_cost]);

    const addColumn = () => {
        dispatch(
            openDialog({
                title: "",
                hidePositiveBtn: true,
                hideNegativeBtn: true,
                hideCloseIcon: true,
                enablePadding: false,
                body: <AddEditColumn onAddNewColumn={(customColumn: CustomCost) => {
                    if (columns.find(c => c.field === `custom_${customColumn.description}`)) {
                        enqueueSnackbar("Column already exists!", { variant: "warning" })
                        return;
                    } else {
                        let col: GridColDef = {
                            field: `custom_${customColumn.description}`,
                            width: 150,
                            sortable: false,
                            hideSortIcons: true,
                            disableColumnMenu: true,
                            editable: true,
                            type: 'number',
                            headerClassName: 'highlight-table-row',
                            // renderHeader: (params: GridColumnHeaderParams) => {
                            //     return (
                            //         <Grid container>
                            //             <Grid item xs={10} sx={{
                            //                 overflow: "clip",
                            //                 textOverflow: "ellipsis"
                            //             }}>
                            //                 <Tooltip title={column.description}>
                            //                     <strong>{column.description}</strong>
                            //                 </Tooltip>
                            //             </Grid>
                            //             <Grid item xs={2}>
                            //                 <IconButton onClick={() => onDeleteColumn(`custom_${column.description}`)}>
                            //                     <Delete color="error" />
                            //                 </IconButton>
                            //             </Grid>
                            //         </Grid>
                            //     );
                            // },
                            renderHeader: (params: GridColumnHeaderParams) => {
                                const type = TypeOptions.find(t => customColumn?.type === t?.value)?.label ?? '';
                                return <CustomCostColumn
                                    tooltip_title={`${customColumn.description} ${customColumn?.subtract === true ? `(${type}-Subtract)` : `(${type}-Add)`}`}
                                    col_name={customColumn.description}
                                    menuOptions={{
                                        editColumn: () => onEditColumn(customColumn),
                                        deleteColumn: () => onDeleteColumn(`custom_${customColumn.description}`)
                                    }}
                                />;
                            },
                            valueGetter: (value, row, column, apiRef) => {
                                return typeof row?.[`custom_${customColumn.description}`] === "object" ? row?.[`custom_${customColumn.description}`]?.value : row?.[`custom_${customColumn.description}`]
                            },
                            // renderCell: (params: GridRenderCellParams) => {
                            //     return <Stack direction={"row"} alignItems={"center"}>
                            //         <InputBase
                            //             required
                            //             placeholder="Enter value"
                            //             type="number"
                            //             disabled={!params?.row?.has_assembly_process_cost && params?.row?.bomId?.includes("BOM")}
                            //             value={params?.row?.[`custom_${column.description}`]?.value}
                            //             onChange={(e) => {
                            //                 setCalculatedRows((prev) => prev.map(p => {
                            //                     if (p.id === params.row.id) {
                            //                         return {
                            //                             ...p, [`custom_${column.description}`]: {
                            //                                 ...p[`custom_${column.description}`],
                            //                                 value: Number(e.target.value),
                            //                             }
                            //                         }
                            //                     }
                            //                     return p;
                            //                 }))
                            //                 setHasCalculated(false);
                            //             }}
                            //             onKeyDown={(event) => {
                            //                 event.stopPropagation();
                            //             }}
                            //         />
                            //         {params?.row?.[`custom_${column.description}`]?.type === "percentage" && <Typography variant="body2">%</Typography>}
                            //     </Stack>
                            // }
                        };

                        setColumns((prev: any[]) => {
                            return ([...prev.slice(0, prev.length - 7), col, ...prev.slice(prev.length - 7, prev.length)]);
                        });

                        setCalculatedRows((prevRows) => prevRows.map((p, idx) => {
                            return { ...p, [`custom_${customColumn.description}`]: { ...customColumn, value: idx === 0 ? null : customColumn.value } }
                        }))

                        setOptions((prev) => [...prev, { label: `${customColumn.description} ${customColumn?.subtract === true ? "(-)" : "(+)"}`, value: `custom_${customColumn.description}` }])

                        setHasCalculated(false);
                    }
                }} />,
            })
        );
    }

    const onEditColumn = (data: CustomCost & { position?: number }) => {
        dispatch(
            openDialog({
                title: "",
                hidePositiveBtn: true,
                hideNegativeBtn: true,
                hideCloseIcon: true,
                enablePadding: false,
                body: <AddEditColumn editDetails={data} onAddNewColumn={(column: CustomCost & { position: number }) => {
                    const updatedCol = {
                        ...column,
                        position: data?.position ?? column.position
                    }
                    setColumns((prev: any[]) => {
                        let index = prev.findIndex(c => c.field === `custom_${data.description}`);
                        let col: GridColDef = {
                            field: `custom_${updatedCol.description}`,
                            width: 150,
                            sortable: false,
                            hideSortIcons: true,
                            disableColumnMenu: true,
                            editable: true,
                            type: 'number',
                            headerClassName: 'highlight-table-row',
                            renderHeader: (params: GridColumnHeaderParams) => {
                                const type = TypeOptions.find(t => updatedCol?.type === t?.value)?.label ?? '';
                                return <CustomCostColumn
                                    tooltip_title={`${updatedCol.description} ${updatedCol?.subtract === true ? `(${type}-Subtract)` : `(${type}-Add)`}`}
                                    col_name={updatedCol.description}
                                    menuOptions={{
                                        editColumn: () => onEditColumn(updatedCol),
                                        deleteColumn: () => onDeleteColumn(`custom_${updatedCol.description}`)
                                    }}
                                />;
                            },
                            valueGetter: (value, row, column, apiRef) => typeof row?.[`custom_${updatedCol.description}`] === "object" ? row?.[`custom_${updatedCol.description}`]?.value : row?.[`custom_${updatedCol.description}`],
                        };
                        return ([...prev.slice(0, index), col, ...prev.slice(index + 1, prev.length)]);
                    });

                    setCalculatedRows((prevRows) => prevRows.map((p, idx) => {
                        delete p?.[`custom_${data.description}`];
                        return { ...p, [`custom_${updatedCol.description}`]: { ...updatedCol, value: idx === 0 ? null : updatedCol.value } }
                    }))

                    setOptions((prev) => [...prev.filter(p => p.value !== `custom_${data.description}`), { label: `${updatedCol.description} ${updatedCol?.subtract === true ? "(-)" : "(+)"}`, value: `custom_${updatedCol.description}` }])

                    setHasCalculated(false);
                }} />,
            })
        );
    };

    const openBatchUpdate = () => {
        dispatch(
            openDialog({
                title: "",
                hidePositiveBtn: true,
                hideNegativeBtn: true,
                enablePadding: false,
                body: <BatchUpdateColumn onBatchUpdate={(selectedOption: string, value: number) => applyValue(selectedOption, value)} fieldOptions={fieldOptions} />,
            })
        );
    }

    const applyValue = (selectedOption: string, enteredValue: number) => {
        setCalculatedRows((prev) => prev.map((p, idx) => {
            if ((!p.has_assembly_process_cost && p.hasOwnProperty('has_assembly_process_cost')) || idx === 0 || (rowSelectionModel.length > 0 && !rowSelectionModel.includes(p.id))) {
                return p;
            }
            const field = selectedOption?.includes("custom_") ? {
                [selectedOption]: {
                    ...p[selectedOption],
                    value: enteredValue,
                }
            } : { [selectedOption]: enteredValue }
            return {
                ...p, ...field
            }
        }))
        setHasCalculated(false);
    };

    const processRowUpdate = (newRow: GridRowModel) => {
        const updatedRow: any = { ...newRow, isNew: false };
        let copiedRows = copy(calculatedRows);
        const colIndex = copiedRows.findIndex((r: { id: string }) => r.id === updatedRow.id);
        let updatedObj = copiedRows[colIndex];
        for (let key in updatedRow) {
            if (key.includes("custom_")) {
                updatedObj[key] = {
                    ...updatedObj[key],
                    value: updatedRow[key]
                }
            } else {
                updatedObj[key] = updatedRow[key];
            }
        }
        copiedRows[colIndex] = updatedObj;
        setCalculatedRows(copiedRows);
        setHasCalculated(false);
        return updatedRow;
    };

    const handleProcessRowUpdateError = React.useCallback((error: Error) => {
        console.error("error:", error);
        enqueueSnackbar(error.message, { variant: "error" });
        // eslint-disable-next-line
    }, []);

    const onSave = async () => {
        dispatch(openBackdrop("Saving the costing details..."));
        const {
            basic_details,
            bom_cost_quantity = 1,
            bom_cost_discount_percentage = 0,
            margin_ratio = 0,
            exchange_rates,
            cost_groups = [],
            other_costs = [],
            discounts = [],
            taxes = [],
        } = pricing;

        await updatePricing({
            pricing_id: id,
            bom_id: basic_details?.bom_id?._id as string,
            payload: {
                bom_cost: updateTreeWithFlattenedData(costing?.calculated_cost ?? pricing?.bom_cost, calculatedRows, columns.filter(c => c.field.includes("custom_"))),
                // bom_id: basic_details?.bom_id?._id as string,
                pricing_name: basic_details.pricing_name,
                pricing_model: basic_details?.pricing_model?.value,
                remarks: basic_details?.remarks,
                bom_cost_quantity,
                bom_cost_discount_percentage,
                margin_ratio,
                exchange_rates,
                cost_groups: cost_groups?.map((c: CostGroup, idx: number) => ({
                    position: idx + 1,
                    title: c.title,
                    quantity: Number(c.quantity) || 1,
                    discount_percentage: Number(c.discount_percentage) || 0,
                    total_amount: Number(c.total_amount) || 0,
                    // value: c.value,
                    costs: c.costs?.map((cost, idx: number) => ({
                        position: idx + 1,
                        description: cost.description,
                        units: cost.units,
                        value: Number(cost.value) || 0,
                        quantity: Number(cost.quantity) || 0,
                        total_cost: Number(cost.total_cost) || 0
                    })) ?? []
                })) ?? [],
                other_costs: other_costs?.map((d: CustomCost, idx: number) => ({
                    position: idx + 1,
                    description: d.description,
                    type: d.type,
                    value: Number(d.value) || 0,
                    subtract: d.subtract ?? false
                })) ?? [],
                discounts: discounts?.map((d: CustomCost, idx: number) => ({
                    position: idx + 1,
                    description: d.description,
                    type: d.type,
                    value: Number(d.value) || 0,
                    subtract: d.subtract ?? false
                })) ?? [],
                taxes: taxes?.map((d: CustomCost, idx: number) => ({
                    position: idx + 1,
                    description: d.description,
                    type: d.type,
                    value: Number(d.value) || 0,
                    subtract: d.subtract ?? false
                })) ?? [],
                // links: [{ object_type: getObjectType(location) as string, object_ids: [p_id] }]
            }
        })
            .unwrap()
            .then((res) => {
                refetch && refetch();
                onCalculate(exchange_rates as ExchangeData);
            })
            .catch((error) => {
                enqueueSnackbar(error?.data?.title ?? "Unable to update the pricing", { variant: "error" });
                console.error(`Error updating pricing: ${JSON.stringify(error)}`)
            })
            .finally(() => {
                dispatch(closeBackdrop());
            })
    }

    const onCalculate = async (exchange_rates: ExchangeData) => {
        dispatch(openBackdrop("Calculating Cost..."));
        await calculateCost({ pricing_id: id, exchange_rates })
            .unwrap()
            .then((res) => {
                refetchCosting();
                setHasCalculated(true);
            }).catch((error) => {
                if (error?.data?.title === "BOM Outdated" && error?.data?.status === 409) {
                    dispatch(openConfirmationDialog({
                        title: "Pricing Update",
                        body: "The BOM of this pricing is updated, do you want to update the pricing with latest BOM changes ?",
                        negativeBtn: "Cancel",
                        positiveBtn: "Yes",
                        onOk: () => {
                            pricingOutdatedUpdateFn(getBOMTree, updatePricing, dispatch, pricing, id, () => {
                                if (refetch) {
                                    refetch();
                                }
                                refetchCosting();
                            });
                        },
                        onNegativeBtn: () => dispatch(closeConfirmationDialog())
                    }))
                }
                enqueueSnackbar(error?.data?.title ?? "Unable to calculate the pricing", { variant: "error" });
                console.error(`Error calculating pricing: ${JSON.stringify(error)}`)
            }).finally(() => {
                dispatch(closeBackdrop());
            });
    }

    const onDelete = (field: string) => {
        if (columns.length > 0) {
            setColumns((prevCols) => prevCols.filter(c => c.field !== field));
        }
        if (fieldOptions.length > 0) {
            setOptions((prev) => prev.filter(p => p.value !== field));
        }
        if (calculatedRows.length > 0) {
            setCalculatedRows((prevRows) => prevRows.map((row) => {
                let copiedRow = copy(row);
                copiedRow[field] = undefined;
                delete copiedRow[field];
                return copiedRow;
            }));
        }
        setHasCalculated(false);
        dispatch(closeConfirmationDialog());
    };

    const onDeleteColumn = (field: string) => {
        dispatch(openConfirmationDialog({
            title: "Delete Operation",
            body: `Are you sure want to delete this column "${field.replace("custom_", "")}"?`,
            positiveBtn: "Delete",
            negativeBtn: "Cancel",
            onOk: () => onDelete(field),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }));
    };

    useEffect(() => {
        if (Object.keys(costing.calculated_cost || {}).length > 0) {
            massageRows(costing.calculated_cost);
            const custom = costing?.calculated_cost ? (findFirstCustomCosts(costing.calculated_cost) ?? []) : [];
            let cols = custom?.map((cur: CustomCost): GridColDef => ({
                field: `custom_${cur.description}`,
                width: 150,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                editable: true,
                type: 'number',
                headerClassName: 'highlight-table-row',
                // renderHeader: (params: GridColumnHeaderParams) => {
                //     return (
                //         <Grid container>
                //             <Grid item xs={10} sx={{
                //                 overflow: "clip",
                //                 textOverflow: "ellipsis"
                //             }}>
                //                 <Tooltip title={cur.description}>
                //                     <strong>{cur.description}</strong>
                //                 </Tooltip>
                //             </Grid>
                //             <Grid item xs={2}>
                //                 <IconButton onClick={() => onDeleteColumn(`custom_${cur.description}`)}>
                //                     <Delete color="error" />
                //                 </IconButton>
                //             </Grid>
                //         </Grid>
                //     );
                // },
                renderHeader: (params: GridColumnHeaderParams) => {
                    const type = TypeOptions.find(t => cur?.type === t?.value)?.label ?? '';
                    return <CustomCostColumn
                        tooltip_title={`${cur.description} ${cur?.subtract === true ? `(${type}-Subtract)` : `(${type}-Add)`}`}
                        col_name={cur.description}
                        menuOptions={readOnly ? undefined : {
                            editColumn: () => onEditColumn(cur),
                            deleteColumn: () => onDeleteColumn(`custom_${cur.description}`)
                        }}
                    />;
                },
                valueGetter: (value, row, column, apiRef) => typeof row?.[`custom_${cur.description}`] === "object" ? row?.[`custom_${cur.description}`]?.value : row?.[`custom_${cur.description}`],
                // renderCell: (params: GridRenderCellParams) => {
                //     return <InputBase
                //         required
                //         placeholder="Enter value"
                //         type="number"
                //         disabled={!params?.row?.has_assembly_process_cost && params?.row?.bomId?.includes("BOM")}
                //         value={params?.row?.[`custom_${cur.description}`]?.value}
                //         onChange={(e) => {
                //             setCalculatedRows((prev) => prev.map(p => {
                //                 if (p.id === params.row.id) {
                //                     return {
                //                         ...p, [`custom_${cur.description}`]: {
                //                             ...p[`custom_${cur.description}`],
                //                             value: Number(e.target.value),
                //                         }
                //                     }
                //                 }
                //                 return p;
                //             }))
                //             setHasCalculated(false);
                //         }}
                //         onKeyDown={(event) => {
                //             event.stopPropagation();
                //         }}
                //     />
                // }
            })) ?? [];
            setColumns([...commonColumns.slice(0, commonColumns.length - 7), ...cols, ...commonColumns.slice(commonColumns.length - 7, commonColumns.length)]);

            setOptions([...cols.map((c: GridColDef) => {
                const foundCost = custom.find((cc: CustomCost) => c.field.split('custom_').includes(cc.description || ""));
                return ({ label: `${c.field.split('custom_').filter(c => c).join("")} ${foundCost?.subtract === true ? "(-)" : "(+)"}`, value: c.field })
            }), defaultOption]);

            setDetails([
                {
                    label: `Cost Breakup for: `,
                    value: `${costing?.calculated_cost?.short_description} (${costing?.calculated_cost?.sequence_id ?? ""
                        }-r${costing?.calculated_cost?.version ?? 0})`,
                },
                {
                    label: `Total Cost (after tax): `,
                    value: `${formatCurrency(costing?.final_price ?? 0, costing?.pricing_config?.exchange_rates?.base_currency, false)}`,
                },
                {
                    label: `Calculated By: `,
                    value: `${costing?.created_user}`,
                },
                {
                    label: `Calculated At: `,
                    value: `${moment(costing?.created_time).format("lll")}`,
                },
                {
                    label: `Pricing ID: `,
                    value: pricing?.sequenceId ?? "",
                }
            ]);
        }
        // eslint-disable-next-line
    }, [JSON.stringify(costing), readOnly]);

    useEffect(() => {
        setHasCalculated(hasCosting);
        // eslint-disable-next-line 
    }, [hasCosting])

    useEffect(() => {
        setColumns((prevCols: any) => {
            let cols = prevCols.filter((p: any) => p?.field?.includes("custom_"));
            return [...commonColumns.slice(0, commonColumns.length - 7), ...cols, ...commonColumns.slice(commonColumns.length - 7, commonColumns.length)]
        })
        // eslint-disable-next-line
    }, [hasCalculated])

    useEffect(() => {
        if (refetch) {
            refetch();
        }
        // eslint-disable-next-line
    }, [])

    // eslint-disable-next-line
    const COLUMNS = React.useMemo(() => columns, [columns]);

    return <MyPaper height={"100%"}>
        <Stack spacing={2} height={"100%"} overflow={"hidden"}>
            <Stack gap={2}>
                {validationErrors.length > 0 &&
                    <MyPaper padding={0}>
                        <Stack
                            sx={{
                                maxHeight: 250,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden' // Prevent outer scrollbar
                            }}
                        >
                            {/* Static Header */}
                            <Stack
                                direction={"row"}
                                onClick={() => updateOpen(!open)}
                                justifyContent={"space-between"}
                                alignItems={"center"}
                                sx={{
                                    flexShrink: 0, // Prevent header from shrinking
                                    cursor: "pointer",
                                    px: 2,
                                    py: 1,
                                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                                }}
                            >
                                <Stack direction={"row"} spacing={1} alignItems={{ xs: "flex-start", md: "center" }}>
                                    <Warning color={"error"} />
                                    <Typography variant="body1" fontFamily={"htrts_medium"}>
                                        Warning: Validation issues encountered during calculation
                                    </Typography>
                                </Stack>

                                <IconButton size={"small"} onClick={(e) => updateOpen(!open)}>
                                    {open ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Stack>

                            {/* Scrollable Content */}
                            <Collapse
                                sx={{
                                    flexGrow: 1,
                                    overflowY: 'auto',
                                    height: 0, // Important for flex to work correctly
                                    '&::-webkit-scrollbar': {
                                        width: '8px' // Thin scrollbar
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent' // Transparent track
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: 'rgba(0,0,0,0.2)', // Subtle scrollbar
                                        borderRadius: '4px'
                                    }
                                }}
                                in={open}
                                timeout="auto"
                                unmountOnExit
                            >
                                <Box sx={{ pr: 1 }}>
                                    <ErrorComponent errors={validationErrors} />
                                </Box>
                            </Collapse>
                        </Stack>
                    </MyPaper>
                }
                <SectionPaper isExpand={false} stack title={"Costing Details"} renderButtons={readOnly ? [] : [
                    <Stack sx={{ width: isMobile ? "100%" : "auto" }} direction={{ sm: 'column', md: "row" }} gap={1}>
                        <Button
                            fullWidth={isMobile}
                            variant="outlined"
                            onClick={openBatchUpdate}
                        >
                            Batch Update
                        </Button>
                        <Button
                            fullWidth={isMobile}
                            variant="contained"
                            onClick={addColumn}
                        >
                            Add Column
                        </Button>
                        <LoadingButton
                            fullWidth={isMobile}
                            variant="contained"
                            onClick={onSave}
                            loading={isLoading}
                        >
                            Save & Calculate
                        </LoadingButton>
                    </Stack>
                ]}>
                    <Grid container alignItems={"center"}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack gap={1}>
                                {bomCostingDetails.map(detail => (<Stack gap={1} direction={{ sm: "column", md: "row" }}>
                                    <Typography variant="body2">{detail.label}</Typography>
                                    <Typography variant="body2"><b>{detail.value}</b></Typography>
                                </Stack>))}
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }} justifyContent={"center"} alignItems={"center"}>
                            {isLoading ? <CircularProgress size={100} color="primary" /> : <Chart
                                chartType="PieChart"
                                data={levelsData}
                                options={{
                                    ...chartOptions,
                                    textColor: 'red',
                                    title: `BOM First Level Details (${costing?.pricing_config?.exchange_rates?.base_currency})`,
                                }}
                                width={"100%"}
                                height={"250px"}
                            />}
                        </Grid>
                    </Grid>
                </SectionPaper>
            </Stack>
            <Stack flexGrow={1} sx={{ overflow: "auto" }}>
                <StyledDataGrid
                    saveLocal={false}
                    density={'compact'}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                sum_of_assm_procs_final_total_cost_in_parent_currency: false,
                                total_assm_procs_currency: false,
                                total_child_bom_cost: false,
                                total_child_bom_currency: false,
                                total_child_material_cost: false,
                                total_child_material_currency: false,
                                total_bom_cost: false,
                                total_bom_currency: false,
                                material_cost: false,
                                material_currency: false,
                                override_cost: false,
                                override_currency: false,
                                spare_part_quantity: false,
                                normal_quantity: false,
                                cost_in_bom_currency: false,
                                bom_currency: false,
                                unit_cost_in_pricing_currency: false,
                            },
                        },
                        pinnedColumns: {
                            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "bom", "short_description"]
                        },
                    }}
                    sx={{
                        '.MuiDataGrid-virtualScroller': {
                            overflow: 'auto !important',
                            height: "auto !important"
                        },
                        '.MuiDataGrid-columnHeaderTitleContainer': {
                            width: "100% !important",
                        },
                        '.MuiDataGrid-columnHeaderTitleContainerContent': {
                            width: "100% !important",
                        },
                        '.MuiDataGrid-cell--textRight': {
                            // justifyContent: 'flex-start !important'
                        }
                    }}
                    showToolbar={true}
                    rows={calculatedRows}
                    columns={COLUMNS}
                    editMode="row"
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    checkboxSelection={true}
                    onRowSelectionModelChange={(newRowSelectionModel) =>
                        setRowSelectionModel(newRowSelectionModel)
                    }
                    isRowSelectable={(row) => costing?.calculated_cost?._id !== row.id}
                    rowSelectionModel={rowSelectionModel}
                    disableChildrenFiltering
                    getRowId={(row) => row?.id}
                    hideFooter={true}
                    pagination={false}
                    isCellEditable={(params) => params.row.id !== costing?.calculated_cost?._id}

                />
                <Typography variant="caption">* Double-click a cell to edit the row, then use the Tab key to move around the table. Press Enter to finish editing.</Typography>
            </Stack>
        </Stack>
    </MyPaper>
}